type AnyObject = Record<string, unknown>;

export type DeepPartial<T> = T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T;

function isPlainObject(value: unknown): value is AnyObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Recursively merges `patch` on top of `base`. Plain-object values are merged
 * depth-first; all other values (arrays, primitives, null) are taken from
 * `patch` when present, otherwise from `base`.
 */
export function deepMerge<T extends object>(base: T, patch: DeepPartial<T>): T {
  const result = { ...base } as AnyObject;
  const patchObj = patch as AnyObject;
  const baseObj = base as AnyObject;

  for (const key of Object.keys(patchObj)) {
    const patchVal = patchObj[key];
    const baseVal = baseObj[key];

    result[key] =
      isPlainObject(patchVal) && isPlainObject(baseVal)
        ? deepMerge(baseVal, patchVal)
        : patchVal;
  }

  return result as T;
}
